import React from 'react';
import { Field } from '@atlaskit/form';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import { InvoiceGroup, InvoiceGroupPurchaseOrder, PaymentMethodType } from '@css-xp/graphtypes';
import BillingAddressDetailsLine from './billing-address-details-line';
import { PaymentMethodSelectDetailsLine } from '../../common-tabs';
import { startCase } from 'lodash';
import { isJiraProduct } from '@atlassian/cssxp-utils-product';
import './invoice-group-form-field-v2.scss';

export interface InvoiceGroupSelectItemV2 {
  value: string; // invoiceGroupId
  invoiceGroup: InvoiceGroup;
  label: React.ReactNode;
}

const getPurchaseOrderDetails = (purchaseDetails: InvoiceGroupPurchaseOrder) => {
  if (purchaseDetails?.oneTimeUse) {
    return purchaseDetails.number + ' ' + '(Single-use)';
  }
  return purchaseDetails.number + ' ' + '(Multi-use)';
};

const InvoiceGroupSelectOption = ({ invoiceGroup }: { invoiceGroup: InvoiceGroup }) => {
  const isNameNone = invoiceGroup.name && invoiceGroup.name?.toLowerCase() !== 'none';

  return (
    <section className="dropdown-option" key={`select-option-${invoiceGroup.id}`}>
      <span>
        <strong>
          {isNameNone ? invoiceGroup.name : 'Unnamed invoice group'} ({invoiceGroup.currency})
        </strong>
        <BillingAddressDetailsLine address={invoiceGroup?.shipToParty} noAddressLabel="No shipping address" />
        <PaymentMethodSelectDetailsLine paymentMethod={invoiceGroup.defaultPaymentMethod} />
      </span>
    </section>
  );
};

export interface InvoiceGroupFormFieldV2Props {
  fieldName?: string;
  fieldLabel?: string;
  isRequired?: boolean;
  selectedInvoiceGroup: InvoiceGroupSelectItemV2 | undefined;
  availableInvoiceGroups?: InvoiceGroup[] | null;
  entitlementBillingInvoiceGroupId?: string | null;
  scheduledBillingInvoiceGroupId?: string | null;
  isDisabled?: boolean | false;
  onChange: (item: InvoiceGroupSelectItemV2) => void;
  productId?: string | undefined;
}

export const InvoiceGroupFormFieldV2 = ({
  fieldName = 'invoiceGroupId',
  fieldLabel = 'Billing profile',
  isRequired = false,
  availableInvoiceGroups = [],
  entitlementBillingInvoiceGroupId = null,
  scheduledBillingInvoiceGroupId = null,
  selectedInvoiceGroup,
  isDisabled,
  onChange,
  productId,
}: InvoiceGroupFormFieldV2Props) => {
  const shouldHidePaymentOnTerms = !isDisabled && productId && isJiraProduct(productId);
  if (shouldHidePaymentOnTerms) {
    availableInvoiceGroups =
      availableInvoiceGroups?.filter(
        (invoiceGroup) => invoiceGroup.defaultPaymentMethod?.type !== PaymentMethodType.DEFERRED
      ) ?? [];
  }
  const invoiceGroupSelectItems = (availableInvoiceGroups ?? []).map((invoiceGroup) => {
    return {
      invoiceGroup,
      label: <InvoiceGroupSelectOption invoiceGroup={invoiceGroup} />,
      value: invoiceGroup.id,
    } as InvoiceGroupSelectItemV2;
  });

  const getDefaultBillingGroup = (invoiceGroupId: string | null) => {
    return invoiceGroupId
      ? invoiceGroupSelectItems.find((invoiceGroupItem: InvoiceGroupSelectItemV2) => {
          return invoiceGroupItem.value === invoiceGroupId;
        })
      : null;
  };

  const hasSingleResult = invoiceGroupSelectItems.length === 1;
  const defaultValue =
    selectedInvoiceGroup ||
    getDefaultBillingGroup(scheduledBillingInvoiceGroupId) ||
    getDefaultBillingGroup(entitlementBillingInvoiceGroupId) ||
    (hasSingleResult ? invoiceGroupSelectItems[0] : undefined);

  if (!selectedInvoiceGroup && defaultValue) {
    onChange(defaultValue);
  }

  return (
    <div className="add-on-invoice-group-dropdown-container" style={{ maxWidth: '500px' }}>
      <Field name={fieldName} label={fieldLabel} isRequired={isRequired}>
        {({ fieldProps }) => (
          <div data-testid="add-on-invoice-group-dropdown">
            <DropdownMenu
              {...fieldProps}
              testId="invoice-group-select-dropdown"
              shouldAllowMultilineItems={true}
              triggerType="button"
              trigger={defaultValue?.label ?? (invoiceGroupSelectItems.length < 1 ? 'No billing profile' : 'None')}
              position="bottom left"
              triggerButtonProps={{
                className: 'invoice-group-select-dropdown-btn',
              }}
            >
              <DropdownItemGroup>
                {invoiceGroupSelectItems.map((item) => {
                  return (
                    <DropdownItem
                      data-testid={`select-option-${item.value}`}
                      key={item.value}
                      onClick={() => {
                        onChange(item);
                      }}
                    >
                      {item.label}
                    </DropdownItem>
                  );
                })}
              </DropdownItemGroup>
            </DropdownMenu>
          </div>
        )}
      </Field>
    </div>
  );
};
